import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Nav } from "@/components/Nav";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import StockResearch from "@/pages/StockResearch";
import FundResearch from "@/pages/FundResearch";
import Compare from "@/pages/Compare";
import Portfolio from "@/pages/Portfolio";
import Chat from "@/pages/Chat";
import Academy from "@/pages/Academy";
import EmergencyFund from "@/pages/EmergencyFund";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBFBF9]">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-navy" />
    </div>
  );
}

function Protected() {
  const { user, checked } = useAuth();
  if (!checked) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen bg-[#FBFBF9]">
      <Nav />
      <Outlet />
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return <Loader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route element={<Protected />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/research/stocks" element={<StockResearch />} />
        <Route path="/research/etfs" element={<FundResearch fundType="etf" />} />
        <Route path="/research/mutual" element={<FundResearch fundType="mutual" />} />
        <Route path="/research/money-market" element={<FundResearch fundType="money_market" />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/emergency-fund" element={<EmergencyFund />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
